import { MouseEvent, useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import clsx from 'clsx';

import { Label, LABEL_TYPE } from '@/components/generic';
import { AUDIO, COVER_PHOTO, IMAGE, PLAYLIST, TEXT, TRIBUTE_VIDEO, VIDEO } from '@/constants';
import { getSlotTypeTranslation } from '@/helpers';
import { useMobileScreen } from '@/hooks';
import { DragIndicator, MusicIcon, PhotoIcon, SpeechIcon, TrashIcon, VideoIcon } from '@/resources';
import { Playlists } from '@/resources/icons/sidebar';
import { CeremonyTemplateSlotCardProps } from '@/types/CeremonyTemplateSlotTypes';

export const EventTemplateSlotCard = ({
  type,
  slot,
  handleUpdateSlotOrder,
  deleteCeremonyTemplateSlot,
  onClick,
  isReorderingEnabled,
  setIsReorderingEnabled,
  isEditEnabled = true
}: CeremonyTemplateSlotCardProps) => {
  const slotDiv = useRef<HTMLDivElement | null>(null);
  const [showTrashIcon, setShowTrashIcon] = useState(false);
  const isLeftSideOfTargetDropDiv = useRef<boolean>(false);
  const dropSlotDiv = useRef<HTMLDivElement | null>(null);
  const [clickCount, setClickCount] = useState(0);

  const isMobile = useMobileScreen();

  const styles = {
    backgroundColor: type.backgroundColor,
    borderColor: type.borderColor,
    textColor: type.textColor
  };

  const [, drag] = useDrag({
    type: 'ceremonial-slot',
    item: { id: slot.id, index: slot.order },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    canDrag: isEditEnabled && (!isMobile || isReorderingEnabled)
  });

  const [{ canDrop, isOver }, drop] = useDrop({
    accept: 'ceremonial-slot',
    collect: (monitor: { isOver: () => boolean; canDrop: () => boolean }) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    }),
    canDrop: () => isEditEnabled,
    hover: (item: { index: number; id: string }, monitor) => {
      if (!slotDiv.current) {
        return;
      }
      const hoverBoundingRect = slotDiv.current.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) {
        return;
      }

      const cursorPositionInTarget = clientOffset.x - hoverBoundingRect.left;
      const isActive = canDrop && isOver;

      const dragToLeft = cursorPositionInTarget < hoverBoundingRect.width / 2;
      isLeftSideOfTargetDropDiv.current = dragToLeft;

      if (dropSlotDiv.current && isActive) {
        dropSlotDiv.current.style.borderLeftColor = dragToLeft
          ? 'var(--primary-blue)'
          : 'transparent';
        dropSlotDiv.current.style.borderRightColor = dragToLeft
          ? 'transparent'
          : 'var(--primary-blue)';
      } else if (dropSlotDiv.current && !isActive) {
        dropSlotDiv.current.style.borderLeftColor = 'transparent';
        dropSlotDiv.current.style.borderRightColor = 'transparent';
      }
    },
    drop: (item: { index: number; id: string }) => {
      if (!slotDiv.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = slot.order;

      if (dragIndex === hoverIndex) {
        return;
      }

      handleUpdateSlotOrder?.(dragIndex, hoverIndex);

      item.index = hoverIndex;

      if (dropSlotDiv.current) {
        dropSlotDiv.current.style.borderLeftColor = 'transparent';
        dropSlotDiv.current.style.borderRightColor = 'transparent';
      }
      setIsReorderingEnabled(false);
    }
  });

  const isActive = canDrop && isOver;

  const handleDelete = (e: MouseEvent) => {
    e.stopPropagation();
    deleteCeremonyTemplateSlot?.(slot);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropSlotDiv.current && !dropSlotDiv.current.contains(event.target as Node)) {
        setClickCount(0);
        setShowTrashIcon(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside as unknown as EventListener);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside as unknown as EventListener);
    };
  }, []);

  const showSlotIcon = () => {
    switch (type.assetType.toString()) {
      case IMAGE:
      case COVER_PHOTO:
        return <PhotoIcon fill={type.iconColor} className='size-5' />;
      case VIDEO:
      case TRIBUTE_VIDEO:
        return <VideoIcon fill={type.iconColor} className='size-5' />;
      case AUDIO:
        return <MusicIcon fill={type.iconColor} className='size-5' />;
      case TEXT:
        return <SpeechIcon fill={type.iconColor} className='size-5' />;
      case PLAYLIST:
        return <Playlists style={{ color: type.iconColor }} className='size-5' />;
      default:
        return null;
    }
  };

  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isEditEnabled) {
      return;
    }

    setIsReorderingEnabled(true);
    const target = e.target as HTMLDivElement;
    if (target && target.draggable) {
      target.dispatchEvent(new DragEvent('dragstart', { bubbles: true }));
    }
  };

  const handleTouchEnd = () => {
    setIsReorderingEnabled(false);
  };

  drag(drop(dropSlotDiv));

  const handleClick = () => {
    if (!isEditEnabled) {
      onClick?.();
      return;
    }

    const clickCountLocal = clickCount + 1;
    if (clickCountLocal % 2 === 1) {
      setShowTrashIcon(true);
    } else {
      onClick?.();
    }
    setClickCount(clickCountLocal);
  };

  return (
    <div
      ref={dropSlotDiv}
      className={clsx(
        'relative h-fit w-1/2 select-none border border-transparent p-2 md:w-1/4',
        !isActive && 'border-0'
      )}
      datatype={slot.order.toString()}
      onClick={isMobile ? handleClick : onClick}>
      {showTrashIcon && isEditEnabled && (
        <div
          className='absolute bottom-0.5 left-0.5 z-50 flex h-10 w-9 items-center justify-center rounded-bl-2xl rounded-tr-xl bg-white/[0.6] pb-2 pl-1 md:hidden'
          onTouchStart={(e) => handleTouchStart(e)}
          onTouchEnd={handleTouchEnd}>
          <DragIndicator className='h-7 w-6 text-primary-blue' />
        </div>
      )}
      <div
        id={'slot-' + slot.id}
        ref={slotDiv}
        className={clsx(
          'relative flex aspect-square flex-col items-center justify-center rounded-2xl border',
          isEditEnabled ? 'cursor-grab' : 'cursor-default',
          isEditEnabled && 'active:cursor-grabbing',
          isActive && 'filter-pop-up-shadow'
        )}
        style={styles}
        onMouseEnter={(e) => {
          e.stopPropagation();
          !isMobile && isEditEnabled && setShowTrashIcon(true);
        }}
        onMouseLeave={() => setShowTrashIcon(false)}>
        <div className='flex flex-col items-center'>
          <img alt='' src={type.iconUrl} />
          {showSlotIcon()}
          <Label text={getSlotTypeTranslation(type.id)} type={LABEL_TYPE.PARAGRAPH2} />
          {showTrashIcon && isEditEnabled && deleteCeremonyTemplateSlot && (
            <div
              className={clsx(
                'absolute right-2 top-2 z-10 flex size-6 cursor-pointer items-center justify-center rounded-md bg-white/[0.6] hover:bg-white'
              )}>
              <TrashIcon
                className={clsx(
                  'absolute right-1 top-1 size-4 cursor-pointer rounded-md bg-white/[0.6] hover:bg-white'
                )}
                onClick={handleDelete}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
